import { isJsonMediaType } from '@scalar/helpers/http/is-json-media-type';
import { joinUrlAndQuery, normalizeMethod } from '../../../libs/http.js';
import { dispositionValue } from '../../../libs/prepare-request.js';
/**
 * http/http1.1
 */
export const httpHttp11 = {
    target: 'http',
    client: 'http1.1',
    title: 'HTTP/1.1',
    generate(request) {
        // Defaults
        const normalizedRequest = {
            method: 'GET',
            headers: [],
            queryString: [],
            ...request,
        };
        // Normalize method
        normalizedRequest.method = normalizeMethod(normalizedRequest.method);
        // Parse URL with error handling
        let url;
        let path;
        try {
            url = new URL(normalizedRequest.url || 'http://');
            path = url.pathname + (url.search || '');
        }
        catch (_error) {
            // Oops, got an invalid URL, use the provided URL directly.
            path = normalizedRequest.url || '/';
        }
        // `host` keeps a non-default port (e.g. `localhost:8080`), as the Host header requires
        const host = url?.host || 'UNKNOWN_HOSTNAME';
        // Start building the request
        let requestString = `${normalizedRequest.method} ${joinUrlAndQuery(path, normalizedRequest.queryString)} HTTP/1.1\r\n`;
        // Store all headers
        const headers = new Map();
        // Host header
        headers.set('Host', host);
        // Passed headers
        normalizedRequest.headers.forEach((header) => {
            if (headers.has(header.name)) {
                headers.set(header.name, `${headers.get(header.name)}, ${header.value}`);
            }
            else {
                headers.set(header.name, header.value);
            }
        });
        // Request body
        let body = '';
        if (normalizedRequest.postData) {
            // Always set the Content-Type header based on postData.mimeType
            if (isJsonMediaType(normalizedRequest.postData.mimeType) && normalizedRequest.postData.text) {
                // Keep the media type as written, so `application/vnd.api+json` or a charset stays intact
                headers.set('Content-Type', normalizedRequest.postData.mimeType);
                body = normalizedRequest.postData.text;
            }
            else if (normalizedRequest.postData.mimeType === 'application/octet-stream' &&
                normalizedRequest.postData.text) {
                headers.set('Content-Type', 'application/octet-stream');
                body = normalizedRequest.postData.text;
            }
            else if (normalizedRequest.postData.mimeType === 'application/x-www-form-urlencoded' &&
                normalizedRequest.postData.params) {
                const formData = normalizedRequest.postData.params
                    .map((param) => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value ?? '')}`)
                    .join('&');
                headers.set('Content-Type', 'application/x-www-form-urlencoded');
                body = formData;
            }
            else if (normalizedRequest.postData.mimeType === 'multipart/form-data' && normalizedRequest.postData.params) {
                const boundary = '{{boundary}}';
                requestString = `// Files and boundary variables use VS Code REST Client syntax.\r\n@boundary = {{$guid}}\r\n\r\n${requestString}`;
                headers.set('Content-Type', `multipart/form-data; boundary=${boundary}`);
                body =
                    normalizedRequest.postData.params
                        .map((param) => {
                        const contentTypeHeader = param.contentType ? `Content-Type: ${param.contentType}\r\n` : '';
                        if (param.fileName !== undefined) {
                            return `--${boundary}\r\nContent-Disposition: form-data; name="${dispositionValue(param.name)}"; filename="${dispositionValue(param.fileName)}"\r\n${contentTypeHeader}\r\n< ${param.fileName}\r\n`;
                        }
                        return `--${boundary}\r\nContent-Disposition: form-data; name="${dispositionValue(param.name)}"\r\n${contentTypeHeader}\r\n${param.value ?? ''}\r\n`;
                    })
                        .join('') + `--${boundary}--\r\n`;
            }
            else if (normalizedRequest.postData.text) {
                // Any other body (text/plain, application/xml, …) is sent as it is
                if (normalizedRequest.postData.mimeType) {
                    headers.set('Content-Type', normalizedRequest.postData.mimeType);
                }
                body = normalizedRequest.postData.text;
            }
        }
        // Add headers to requestString
        headers.forEach((value, name) => {
            requestString += `${name}: ${value}\r\n`;
        });
        // Add body to requestString
        requestString += `\r\n${body}`;
        return requestString;
    },
};
